/**
 * The canvas editor.
 *
 * Draws the scene with the **shared renderer** and edits the `SceneDoc` that produced it. There is
 * no second layout implementation here: selection outlines come from `layerBounds`, which is the same
 * geometry the renderer used, and every edit mutates the document and re-renders. That is what makes
 * "what the editor shows is what the export contains" a property rather than a hope.
 *
 * Interaction is deliberately split between the canvas and numeric inputs: dragging is good for
 * placing something and bad for reproducing it, so anything a user might want to repeat — a scale, a
 * rotation, a layer's order — is a number or a button they can set exactly.
 */
import { createBrowserEnvironment } from '@keymaker/renderer/browser';
import { renderScene, type LayerBounds, type RenderCanvas } from '@keymaker/renderer';
import type { DeviceFrame, Layer, OutputFamily, SceneDoc } from '@keymaker/schema';
import type { RenderImage } from '@keymaker/renderer';

export interface EditorOptions {
  frames: readonly DeviceFrame[];
  assets: Map<string, RenderImage>;
  onChange: (scene: SceneDoc) => void;
  /** Called when a layer references an asset that is not in the map. */
  onMissingAsset?: (ref: string) => void;
  /**
   * Asked for when the scene needs Tier 3 bodies that have not been built.
   *
   * Building a 3D body is asynchronous, so a change that invalidates one cannot be finished by the
   * synchronous draw the editor would otherwise perform. The editor reports it instead of guessing.
   */
  onNeedsRebuild?: () => void;
  /**
   * Called when the selected layer changes, including from a click on the artwork.
   *
   * The board mounts one editor per panel, so a click on a card has to be able to say "this is the
   * panel the inspector is now driving" — otherwise clicking panel 4 lights up a layer while the
   * inspector still edits panel 1.
   */
  onSelectionChange?: (id: string | null) => void;
}

/** The design's state colour. Duplicated from the stylesheet deliberately: this one is painted into a
 * canvas, which cannot read a custom property, and a second invented blue would drift from it. */
const SELECTION_COLOR = '#1f4bd8';

export class Editor {
  private readonly env = createBrowserEnvironment();
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  /**
   * The compositor's surface, kept and reused.
   *
   * Two things have to be true at once, and the first two attempts each got one of them.
   *
   * A fresh canvas per frame allocated and abandoned fifteen megabytes, which arrived as a single
   * 450 ms collection pause in the middle of a drag — the one frame over the hundred-millisecond
   * budget while everything else sat at seventeen.
   *
   * Drawing straight into the canvas that is on screen removed that, and broke the promise that the
   * download is the preview: a canvas that is read back — and this test harness reads it on every
   * snapshot — gets moved to a CPU surface by the browser, so its pixels started depending on how many
   * times anyone had looked at them. Eighteen per cent of the panel differed, scattered over every edge
   * in it.
   *
   * So the render goes to a surface of its own that is reused, and the result is copied onto the
   * screen. One allocation for the session, one copy per frame, and the pixels on screen are a copy of
   * what the export produces rather than a second rasterisation of it.
   */
  private readonly target: RenderCanvas;
  private readonly overlay: HTMLCanvasElement;
  private scene: SceneDoc | null = null;
  /**
   * Maps the document to what should be drawn.
   *
   * The document keeps `{{caption.1}}` and the panel's asset key intact; this substitutes them for
   * the draw and for nothing else. The first version wrote the resolved copy into the document,
   * which made a user's caption edit indistinguishable from the automatic substitution — the editor
   * showed one thing and the export produced another, and only the pixel comparison noticed.
   */
  private drawAs: (scene: SceneDoc) => SceneDoc = (scene) => scene;
  private drawn: SceneDoc | null = null;
  private devices: Map<string, RenderImage> | null = null;
  /**
   * A signature of the 3D work the current `devices` map actually answers for.
   *
   * The editor can only composite synchronously if it holds bodies that match the scene. Without this
   * it would happily draw a 3D layer using a body prepared for a different rotation — a stale frame
   * that looks entirely plausible — or throw when it held none at all, which is what turned the whole
   * canvas blank the moment a device was switched to 3D.
   */
  private preparedKey: string | null = null;
  private family: OutputFamily | null = null;
  private bounds: LayerBounds[] = [];
  private selectedId: string | null = null;
  private drag: { layerId: string; startX: number; startY: number; originX: number; originY: number } | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly options: EditorOptions,
  ) {
    // The compositor draws into `this.target` and the result is copied here, so what is on screen is a
    // copy of the render rather than a second rasterisation of it.
    this.target = this.env.createCanvas(1, 1);
    this.canvas = document.createElement('canvas');
    this.canvas.dataset.role = 'stage';
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Could not acquire a 2D context for the editor.');
    this.ctx = ctx;

    // The overlay is a separate canvas so the selection outline never becomes part of the artwork.
    this.overlay = document.createElement('canvas');
    this.overlay.dataset.role = 'overlay';
    // Styling lives in the stylesheet, not in inline styles, so the plate stays one designed object.
    this.overlay.className = 'overlay';

    // The plate is the positioned parent; no wrapper element, so the artwork and its outline are the
    // only two things inside it.
    this.root.append(this.canvas, this.overlay);

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
  }

  get selection(): string | null {
    return this.selectedId;
  }

  select(id: string | null): void {
    this.setSelection(id);
    this.options.onChange(this.scene!);
  }

  /** The one place the selection is written, so the change is always reported. */
  private setSelection(id: string | null): void {
    if (this.selectedId === id) return;
    this.selectedId = id;
    this.drawOverlay();
    this.options.onSelectionChange?.(id);
  }

  setScene(
    scene: SceneDoc,
    family: OutputFamily,
    drawAs?: (scene: SceneDoc) => SceneDoc,
    devices?: Map<string, RenderImage>,
  ): void {
    this.scene = scene;
    this.family = family;
    if (drawAs) this.drawAs = drawAs;
    // Resolved by the caller, because building a 3D body is asynchronous and `renderScene` is not:
    // keeping the compositor synchronous is what lets all three environments share one renderer.
    this.devices = devices ?? null;
    this.preparedKey = Editor.signatureOf(scene);
    this.render();
  }

  /** The document being edited, with its tokens unsubstituted. */
  getScene(): SceneDoc | null {
    return this.scene;
  }

  /** The resolved view, which is what the user is looking at. */
  getDrawnScene(): SceneDoc | null {
    return this.drawn;
  }

  /**
   * What the prepared bodies are a function of. `rotation` is in here because a 3D body is built turned,
   * not turned at draw time, so changing it invalidates the body.
   */
  private static signatureOf(scene: SceneDoc): string | null {
    const devices = scene.layers.filter(
      (layer): layer is Extract<Layer, { type: 'device' }> => layer.type === 'device' && layer.kind === '3d',
    );
    if (devices.length === 0) return null;
    return JSON.stringify(
      devices.map((layer) => [layer.id, layer.frame, layer.screen, layer.perspective, layer.body, layer.transform.rotation]),
    );
  }

  /** Compose and draw, or hand the work back when a 3D body has to be built before the scene is true. */
  private compose(): void {
    if (!this.scene) return;
    if (Editor.signatureOf(this.scene) !== this.preparedKey) {
      this.options.onNeedsRebuild?.();
      return;
    }
    this.render();
  }

  /** Replace one layer, keeping the document immutable so undo and comparison stay trivial. */
  updateLayer(id: string, patch: (layer: Layer) => Layer): void {
    if (!this.scene) return;
    const layers = this.scene.layers.map((layer) => (layer.id === id ? patch(layer) : layer));
    this.scene = { ...this.scene, layers };
    this.compose();
    this.options.onChange(this.scene);
  }

  /** Move a layer within the paint order. `+1` raises it, `-1` lowers it. */
  reorder(id: string, delta: number): void {
    if (!this.scene) return;
    const layers = [...this.scene.layers];
    const index = layers.findIndex((layer) => layer.id === id);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= layers.length) return;
    const [moved] = layers.splice(index, 1);
    layers.splice(target, 0, moved!);
    this.scene = { ...this.scene, layers };
    this.compose();
    this.options.onChange(this.scene);
  }

  toggleVisible(id: string): void {
    this.updateLayer(id, (layer) => ({ ...layer, visible: !layer.visible }));
  }

  render(): void {
    if (!this.scene || !this.family) return;
    const { width, height } = this.scene.canvas;

    this.canvas.width = width;
    this.canvas.height = height;
    this.overlay.width = width;
    this.overlay.height = height;
    // Display size is CSS, so the backing store stays at export resolution and the preview is a
    // scaled view of the real thing rather than a different render.
    // Display size is deliberately **not** set here, and the backing store stays at export
    // resolution so the preview is a scaled view of the real thing rather than a different render.
    // The first version measured the stage from JavaScript and wrote a pixel width; when that
    // measurement came back before layout it produced a negative scale, the invalid CSS was ignored,
    // and the canvas silently kept its full 1320px intrinsic width — so the artwork ran off the
    // bottom of the stage and the panel could not be seen. The stylesheet fits it now, which cannot
    // arrive late, and hit-testing reads the real rect instead of trusting a remembered scale.

    // The surface is sized to the scene before it is drawn into; `renderScene` clears it, so nothing
    // survives from the previous frame.
    const target = this.target.handle as HTMLCanvasElement;
    target.width = width;
    target.height = height;

    this.drawn = this.drawAs(this.scene);
    const result = renderScene(this.drawn, this.env, {
      frames: this.options.frames,
      assets: this.options.assets,
      ...(this.devices && this.devices.size > 0 ? { devices: this.devices } : {}),
      // Reported rather than swallowed. Drawing an empty device silently is how the editor and the
      // export diverged by millions of pixels while both looked like they had worked.
      onMissingAsset: (ref) => this.options.onMissingAsset?.(ref),
      into: this.target,
    });
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, width, height);
    this.ctx.drawImage(this.target.handle as CanvasImageSource, 0, 0);
    this.bounds = result.bounds;

    this.drawOverlay();
  }

  private drawOverlay(): void {
    const octx = this.overlay.getContext('2d');
    if (!octx || !this.scene) return;
    const { width, height } = this.scene.canvas;
    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, width, height);
    if (!this.selectedId) return;

    const bounds = this.bounds.find((entry) => entry.id === this.selectedId);
    if (!bounds) return;
    // Half the hairline the rest of the app uses, because this one is drawn in artwork pixels and is
    // then scaled down: at 3 canvas px it disappeared on a retina-ish fit, at 6 it reads as a rule.
    octx.strokeStyle = SELECTION_COLOR;
    octx.lineWidth = 6;
    octx.setLineDash([18, 14]);
    octx.strokeRect(bounds.rect.x, bounds.rect.y, bounds.rect.width, bounds.rect.height);
    octx.setLineDash([]);
  }

  /** Canvas pixel coordinates for a pointer event, accounting for the display scale. */
  private toScene(event: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / (rect.width / this.canvas.width),
      y: (event.clientY - rect.top) / (rect.height / this.canvas.height),
    };
  }

  private hitTest(point: { x: number; y: number }): LayerBounds | null {
    // Topmost first: the last layer painted is the one the user sees.
    for (let index = this.bounds.length - 1; index >= 0; index -= 1) {
      const bounds = this.bounds[index]!;
      const { x, y, width, height } = bounds.rect;
      if (point.x >= x && point.x <= x + width && point.y >= y && point.y <= y + height) return bounds;
    }
    return null;
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (!this.scene) return;
    const point = this.toScene(event);
    const hit = this.hitTest(point);
    this.setSelection(hit?.id ?? null);
    this.options.onChange(this.scene);

    if (!hit) return;
    const layer = this.scene.layers.find((entry) => entry.id === hit.id);
    if (!layer) return;
    const origin = layerOrigin(layer);
    if (!origin) return;

    this.drag = { layerId: hit.id, startX: point.x, startY: point.y, originX: origin.x, originY: origin.y };
    this.canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.drag || !this.scene || !this.family) return;
    const point = this.toScene(event);
    const dx = (point.x - this.drag.startX) / this.scene.canvas.width;
    const dy = (point.y - this.drag.startY) / this.scene.canvas.height;

    this.updateLayer(this.drag.layerId, (layer) => {
      if (layer.type === 'device') {
        return { ...layer, transform: { ...layer.transform, x: clamp01(this.drag!.originX + dx), y: clamp01(this.drag!.originY + dy) } };
      }
      const box = layer.box;
      return { ...layer, box: { ...box, x: clamp01(this.drag!.originX + dx), y: clamp01(this.drag!.originY + dy) } };
    });
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (!this.drag) return;
    this.drag = null;
    if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
  };
}

/** The normalised position a drag starts from: transform for a device, box for everything else. */
function layerOrigin(layer: Layer): { x: number; y: number } | null {
  if (layer.type === 'device') return { x: layer.transform.x, y: layer.transform.y };
  if (layer.type === 'text' || layer.type === 'image' || layer.type === 'shape') {
    return { x: layer.box.x, y: layer.box.y };
  }
  return null;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, Number(value.toFixed(4))));
}
